/**
 * Scenario Save Controller (EPIC-027 / 27.5)
 *
 * Drives the "Sauvegarder ce scénario" CTA on /simulateur-ajout-activite.html.
 * Two buttons (left `nm-actions`, right `nm-card--surface`) share this single
 * controller. It opens a modal, accepts a title, then POSTs to
 * `/simulator/scenarios` with `kind = "add_activity"`.
 *
 * UX/ARIA:
 *  - role="dialog", aria-modal="true"
 *  - Focus trap (Tab / Shift+Tab cycle within the modal).
 *  - ESC closes.
 *  - 409 → cap-10 message inline.
 *  - 422 → field-level error under the title input.
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";

export default class ScenarioSaveController extends BaseController {
  static targets = [
    "modal",
    "titleInput",
    "titleError",
    "globalError",
    "submitBtn",
  ];

  connect() {
    this.onKeyDown = this.onKeyDown.bind(this);
    this.previouslyFocused = null;
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeyDown);
  }

  open() {
    if (!this.hasModalTarget) return;
    this.previouslyFocused = document.activeElement;
    this.resetErrors();

    if (this.hasTitleInputTarget) {
      this.titleInputTarget.value = defaultTitle();
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = false;
      this.submitBtnTarget.textContent = "Sauvegarder";
    }

    if (typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal();
    } else {
      this.modalTarget.setAttribute("open", "open");
    }
    document.addEventListener("keydown", this.onKeyDown);

    requestAnimationFrame(() => {
      if (this.hasTitleInputTarget) {
        this.titleInputTarget.focus();
        this.titleInputTarget.select();
      }
    });
  }

  close() {
    if (!this.hasModalTarget) return;
    if (typeof this.modalTarget.close === "function") {
      this.modalTarget.close();
    } else {
      this.modalTarget.removeAttribute("open");
    }
    document.removeEventListener("keydown", this.onKeyDown);
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === "function") {
      this.previouslyFocused.focus();
    }
  }

  onKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === "Tab") {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    const focusable = this.collectFocusable();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  collectFocusable() {
    if (!this.hasModalTarget) return [];
    return Array.from(
      this.modalTarget.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.hidden && el.offsetParent !== null);
  }

  async submit(event) {
    event.preventDefault();
    this.resetErrors();

    const title = this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : "";
    if (title === "") {
      this.showTitleError("Le nom du scénario est requis.");
      this.titleInputTarget?.focus();
      return;
    }
    if (title.length > 120) {
      this.showTitleError("Le nom doit faire 120 caractères maximum.");
      this.titleInputTarget?.focus();
      return;
    }

    const formCtrl = this.findFormController();
    if (!formCtrl) {
      this.showGlobalError("Impossible de récupérer les paramètres du scénario.");
      return;
    }

    const inputs = formCtrl.getInputsForScenarioSave();
    const outputs = formCtrl.getLastResult();

    if (!outputs) {
      this.showGlobalError(
        "Lancez d'abord un calcul avant de sauvegarder le scénario.",
      );
      return;
    }

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true;
      this.submitBtnTarget.textContent = "Sauvegarde…";
    }

    try {
      await api.post("/simulator/scenarios", {
        title,
        kind: "add_activity",
        inputs,
        outputs,
      });
      this.showSuccess(`Scénario « ${title} » sauvegardé.`);
      this.close();
    } catch (err) {
      if (err && err.status === 409) {
        this.showGlobalError(
          err.data?.error?.message
            || "Vous avez atteint la limite de 10 scénarios actifs. Archivez-en un avant d'en sauvegarder un nouveau.",
        );
      } else if (err && err.status === 422) {
        const fields = err.data?.error?.details?.fields || [];
        const titleErr = fields.find((f) => f.field === "title");
        if (titleErr) {
          this.showTitleError(titleErr.message || "Titre invalide.");
        } else {
          this.showGlobalError(err.data?.error?.message || "Données invalides.");
        }
      } else if (err && err.status === 403) {
        this.showGlobalError(
          "Ce simulateur est réservé aux micro-entrepreneurs actifs.",
        );
      } else {
        this.showGlobalError("La sauvegarde a échoué. Réessayez.");
      }
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false;
        this.submitBtnTarget.textContent = "Sauvegarder";
      }
    }
  }

  findFormController() {
    const formRoot = document.querySelector(
      '[data-activity-addition-form-target="formColumn"]',
    );
    if (!formRoot) return null;
    return this.application.getControllerForElementAndIdentifier(
      formRoot,
      "activity-addition-form",
    );
  }

  showTitleError(message) {
    if (!this.hasTitleErrorTarget) return;
    this.titleErrorTarget.textContent = message;
    this.titleErrorTarget.hidden = false;
    if (this.hasTitleInputTarget) {
      this.titleInputTarget.setAttribute("aria-invalid", "true");
    }
  }

  showGlobalError(message) {
    if (!this.hasGlobalErrorTarget) return;
    this.globalErrorTarget.textContent = message;
    this.globalErrorTarget.hidden = false;
  }

  resetErrors() {
    if (this.hasTitleErrorTarget) {
      this.titleErrorTarget.textContent = "";
      this.titleErrorTarget.hidden = true;
    }
    if (this.hasGlobalErrorTarget) {
      this.globalErrorTarget.textContent = "";
      this.globalErrorTarget.hidden = true;
    }
    if (this.hasTitleInputTarget) {
      this.titleInputTarget.removeAttribute("aria-invalid");
    }
  }
}

function defaultTitle() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  return `Ajout activité — ${dd}/${mm}/${now.getFullYear()}`;
}
